"use client";

import { useMemo, useState, type ReactNode } from "react";

import PersonalTaraBalamCard, {
  type PersonalTaraBalamData,
} from "@/components/engine-visuals/PersonalTaraBalamCard";
import type { PanchangResponse } from "@/lib/api/panchang";
import {
  NAKSHATRA_DATA,
  normalizeNakshatraKey,
} from "../nakshatra-calculator/_components/nakshatra-data";
import { TARA_BALAM_DEFAULT_CITY } from "./tara-balam.server";

type TaraVerdict = NonNullable<PanchangResponse["bala"]>["tarabalam"][number];

function hindiNakshatra(name: string): string {
  return NAKSHATRA_DATA[normalizeNakshatraKey(name)]?.hi ?? name;
}

/**
 * The day's engine-computed auspicious windows, for the city this page reads.
 *
 * These are day-wide muhurats, not per-seeker windows — the personal part of
 * this page is the Tara verdict above them. Never synthesize a window: Abhijit
 * and Amrit Kaal are derived from local sunrise/sunset and change daily and by
 * city (rules 24/29).
 */
function auspiciousWindows(
  snapshot: PanchangResponse,
  cityName: string
): PersonalTaraBalamData["personalGreenHours"] {
  const windows: PersonalTaraBalamData["personalGreenHours"] = [];

  const abhijit = snapshot.abhijit_muhurta;
  if (abhijit?.start && abhijit.end) {
    windows.push({
      start: abhijit.start,
      end: abhijit.end,
      purposeEn: `Abhijit Muhurat in ${cityName} — the day's self-correcting window, used when an inauspicious Tara cannot be waited out`,
      purposeHi: `${cityName} में अभिजीत मुहूर्त — तारा दोष होने पर भी आवश्यक कार्य हेतु शास्त्रीय परिहार`,
    });
  }

  const amrit = snapshot.amrit_kaal;
  if (amrit?.start && amrit.end) {
    windows.push({
      start: amrit.start,
      end: amrit.end,
      purposeEn: `Amrit Kaal in ${cityName} — scaled from today's nakshatra span, favourable for beginnings`,
      purposeHi: `${cityName} में अमृत काल — आज के नक्षत्र से गणना, नवीन कार्यारंभ हेतु शुभ`,
    });
  }

  return windows;
}

function taraAdvice(quality: string, isHi: boolean): string {
  if (quality === "favourable") {
    return isHi
      ? "आज का गोचर नक्षत्र आपके जन्म नक्षत्र से शुभ तारा बनाता है — महत्वपूर्ण कार्यारंभ अनुकूल है।"
      : "Today's transit star forms a favourable Tara from your birth star — a supportive day to begin what matters.";
  }
  if (quality === "unfavourable") {
    return isHi
      ? "आज विपरीत तारा है — नए दीर्घकालिक संकल्प टालें; आवश्यक कार्य अभिजीत मुहूर्त में करें।"
      : "Today forms an adverse Tara — defer new long-term commitments, and use the Abhijit window if a task cannot wait.";
  }
  return isHi
    ? "आज मिश्रित तारा है — नियमित कार्य ठीक हैं; बड़े निर्णय शुभ मुहूर्त में लें।"
    : "Today forms a mixed Tara — routine work is fine; place bigger decisions inside an auspicious window.";
}

export default function TaraBalamClient({
  language = "en",
  snapshot,
  crossLinks,
}: {
  language?: "en" | "hi";
  snapshot: PanchangResponse | null;
  crossLinks?: ReactNode;
}) {
  const isHi = language === "hi";
  const cityName = TARA_BALAM_DEFAULT_CITY.name;
  const verdicts: TaraVerdict[] = useMemo(() => snapshot?.bala?.tarabalam ?? [], [snapshot]);

  const [selectedIndex, setSelectedIndex] = useState<number>(0);
  const selected = verdicts.find((row) => row.nakshatra_index === selectedIndex) ?? verdicts[0];

  const taraData: PersonalTaraBalamData | null = useMemo(() => {
    if (!snapshot || !selected) return null;

    return {
      birthNakshatraEn: selected.nakshatra,
      birthNakshatraHi: hindiNakshatra(selected.nakshatra),
      transitNakshatraEn: snapshot.nakshatra.name,
      transitNakshatraHi: hindiNakshatra(snapshot.nakshatra.name),
      taraNameEn: selected.tara,
      taraNameHi: selected.tara_hi,
      isAuspicious: selected.quality === "favourable",
      scoreOutOf9: selected.tara_number,
      personalGreenHours: auspiciousWindows(snapshot, cityName),
      adviceEn: taraAdvice(selected.quality, false),
      adviceHi: taraAdvice(selected.quality, true),
    };
  }, [snapshot, selected, cityName]);

  return (
    <div className="sacred-bg min-h-[calc(100vh-130px)]">
      <div className="mx-auto max-w-2xl px-4 py-10 sm:px-6 sm:py-14">
        <div className="mb-8 text-center">
          <p className="mb-3 text-eyebrow font-semibold uppercase tracking-eyebrow text-intent-brand">
            {isHi ? "दैनिक व्यक्तिगत ताराबल" : "Daily Personal Tara Balam"}
          </p>
          <h1 className="mb-3 font-serif text-2xl font-bold leading-tight text-text-primary sm:text-3xl">
            {isHi
              ? "ताराबल कैलकुलेटर: जानें आज आपका नक्षत्र बल कैसा है"
              : "Tara Balam Calculator: Today's Star Strength for Your Nakshatra"}
          </h1>
          <p className="mx-auto max-w-lg text-xs leading-relaxed text-text-secondary sm:text-sm">
            {isHi
              ? "अपना जन्म नक्षत्र चुनें और आज के गोचर नक्षत्र से गिनी गई 9 ताराओं में अपना बल देखें।"
              : "Select your birth star to see which of the 9 Taras it forms with today's transit nakshatra."}
          </p>
        </div>

        {taraData && selected ? (
          <>
            <div className="card-sacred mb-6 p-5 sm:p-6">
              <label
                htmlFor="tara-balam-nakshatra"
                className="mb-2 block text-xs font-bold uppercase tracking-wide text-text-primary"
              >
                {isHi ? "अपना जन्म नक्षत्र चुनें:" : "Select Your Birth Nakshatra:"}
              </label>
              <select
                id="tara-balam-nakshatra"
                value={selected.nakshatra_index}
                onChange={(event) => setSelectedIndex(Number(event.target.value))}
                className="w-full rounded-xl border border-border-default bg-white p-3 text-sm font-medium text-text-primary focus:outline-hidden focus:ring-2 focus:ring-intent-brand"
              >
                {verdicts.map((row) => (
                  <option key={row.nakshatra_index} value={row.nakshatra_index}>
                    {row.nakshatra} ({hindiNakshatra(row.nakshatra)})
                  </option>
                ))}
              </select>
              <p className="mt-3 text-caption leading-relaxed text-text-muted">
                {isHi
                  ? `आज का गोचर नक्षत्र ${snapshot?.nakshatra.name} है — ${cityName} के सूर्योदय के अनुसार गणना।`
                  : `Today's transit nakshatra is ${snapshot?.nakshatra.name}, computed for ${cityName}.`}
              </p>
            </div>

            <PersonalTaraBalamCard data={taraData} locale={isHi ? "hi" : "en"} />
          </>
        ) : (
          <div className="card-sacred p-6 text-center text-sm text-text-secondary">
            {isHi
              ? "आज का ताराबल अभी उपलब्ध नहीं है। कृपया कुछ देर बाद पुनः देखें।"
              : "Today's Tara Balam is not available right now. Please check back shortly."}
          </div>
        )}

        {crossLinks}
      </div>
    </div>
  );
}
